import { useCallback, useEffect, useState } from 'react'
import { api } from '../services/api'

const STATUSES = ['New', 'Packed', 'Shipped', 'Delivered', 'Cancelled']
const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`

const asList = (r) => (Array.isArray(r) ? r : Array.isArray(r?.data) ? r.data : Array.isArray(r?.items) ? r.items : [])

function itemsOf(o) {
  let list = o.items
  if (typeof list === 'string') { try { list = JSON.parse(list) } catch { list = [] } }
  return Array.isArray(list) ? list : []
}

const when = (o) => o.created_at ?? o.createdAt ?? o.date ?? ''
const statusOf = (o) => o.status || 'New'

export default function AdminOrders() {
  const [orders, setOrders] = useState(null)
  const [error, setError] = useState('')
  const [filter, setFilter] = useState('All')
  const [busy, setBusy] = useState(null)

  const load = useCallback(async () => {
    try {
      const list = asList(await api.list('orders', true))
      list.sort((a, b) => String(when(b)).localeCompare(String(when(a))) || Number(b.id) - Number(a.id))
      setOrders(list)
      setError('')
    } catch (err) {
      setError(err.message || 'Could not load orders.')
      setOrders((o) => o || [])
    }
  }, [])

  // load now, then check for new orders every 30 seconds
  useEffect(() => {
    load()
    const t = setInterval(load, 30000)
    return () => clearInterval(t)
  }, [load])

  const setStatus = async (o, status) => {
    setBusy(o.id)
    try {
      const fd = new FormData()
      fd.append('status', status)
      await api.update('orders', o.id, fd)
      await load()
    } catch (err) {
      setError(err.message || 'Could not update the order.')
    } finally {
      setBusy(null)
    }
  }

  const shown = (orders || []).filter((o) => filter === 'All' || statusOf(o) === filter)

  return (
    <>
      <h1>Orders</h1>
      <p className="adm__hint">Orders appear here after the customer pays. This list refreshes by itself.</p>

      <div className="adm__ordtools">
        {['All', ...STATUSES].map((s) => (
          <button key={s} type="button" className={`adm__chipbtn${filter === s ? ' is-on' : ''}`} onClick={() => setFilter(s)}>
            {s}{s === 'All' && orders ? ` (${orders.length})` : ''}
          </button>
        ))}
        <button type="button" className="adm__btn adm__btn--ghost" onClick={load}>Refresh</button>
      </div>

      {error && <p className="adm__error" role="alert">{error}</p>}
      {orders === null && <p className="adm__hint">Loading…</p>}
      {orders && shown.length === 0 && !error && <p className="adm__hint">No orders yet.</p>}

      <ul className="adm__orders">
        {shown.map((o) => {
          const items = itemsOf(o)
          const date = when(o) ? new Date(when(o)) : null
          const pay = o.payment_id ?? o.paymentId
          return (
            <li key={o.id} className="adm__card adm__ordcard">
              <div className="adm__ordhead">
                <strong>Order #{o.order_no || o.id}</strong>
                <span className="adm__hint">
                  {date && !isNaN(date) ? date.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : ''}
                </span>
              </div>

              <div className="adm__ordgrid">
                <div>
                  <h4>Customer</h4>
                  <p><strong>{o.customer_name ?? o.name}</strong></p>
                  <p>{o.phone}</p>
                  <p>{o.email}</p>
                </div>
                <div>
                  <h4>{o.delivery_method || 'Delivery'}</h4>
                  <p>{[o.address, o.city, o.pincode].filter(Boolean).join(', ') || 'Pick up at the store'}</p>
                </div>
                <div>
                  <h4>Items</h4>
                  <ul>
                    {items.map((i, k) => <li key={k}>{i.qty} × {i.name} ({inr(i.price)})</li>)}
                  </ul>
                </div>
                <div>
                  <h4>Payment</h4>
                  <p>
                    <strong>{inr(o.total ?? o.amount)}</strong>{' '}
                    <span className={`adm__ordstatus${/unpaid|fail/i.test(o.payment_status || '') ? ' adm__ordstatus--bad' : ''}`}>{o.payment_status || 'Paid'}</span>
                  </p>
                  {pay && <p className="adm__hint">{pay}</p>}
                </div>
              </div>

              <div className="adm__ordfoot">
                <label>
                  Status{' '}
                  <select value={statusOf(o)} disabled={busy === o.id} onChange={(e) => setStatus(o, e.target.value)}>
                    {STATUSES.map((s) => <option key={s}>{s}</option>)}
                  </select>
                </label>
              </div>
            </li>
          )
        })}
      </ul>
    </>
  )
}